import React, { useMemo } from "react";
import { createPortal } from "react-dom";
import { StringParam, useQueryParam, withDefault } from "use-query-params";
import GoogleBusinessAccountDialog from "./GoogleBusinessAccountDialog";
import { ListPage } from "@src/features/app/ListPage";
import GuestFeedbackPageHeader from "./GuestFeedbackPageHeader";
import { gql } from "@apollo/client";
import {
  EmptyState,
  EmptyStateDescription,
  EmptyStateTitle,
} from "@src/lib/ui/EmptyState";
import { Tabs, TabsList, TabsTrigger } from "@src/lib/ui/Tabs";
import { usePortalTarget } from "@src/lib/hooks/usePortalTarget";
import GuestFeedbackReviewsTab from "./GuestFeedbackReviewsTab";
import GuestFeedbackIntegrationsTab from "./GuestFeedbackIntegrationsTab";
import { useOrgGuestFeedbackIntegrationsQuery } from "./GuestFeedbackIntegrationsTab.generated";
import { useOrgGuestFeedbackCsvUploadsQuery } from "./GuestFeedbackCsvUploadsTab.generated";
import { useOrgGuestFeedbackSyncsQuery } from "./GuestFeedbackSyncsTab.generated";
import GuestFeedbackSyncsTab from "./GuestFeedbackSyncsTab";
import GuestFeedbackCsvUploadsTab from "./GuestFeedbackCsvUploadsTab";
import GuestFeedbackUploadBatches from "./GuestFeedbackUploadBatches";
import GuestFeedbackAnalysesTab from "./GuestFeedbackAnalysesTab";
import {
  useGuestFeedbackCsvImport,
  CSV_IMPORT_POLL_INTERVAL_MS,
} from "./useGuestFeedbackCsvImport";
import GuestFeedbackGettingStarted from "./GuestFeedbackGettingStarted";
import useGuestFeedbackSetupStatus from "./hooks/useGuestFeedbackSetupStatus";
import {
  useGuestFeedbackRecommendationsQuery,
  useGuestFeedbackAnalysisRunStatusQuery,
} from "./GuestFeedbackView.generated";
import {
  ActionItemPriority,
  GuestFeedbackCorrectiveActionStatus,
  LocationGroupType,
} from "@src/types.generated";
import { Skeleton } from "@src/lib/ui/Skeleton";
import Spinner from "@src/lib/ui/Spinner";
import { Card, CardContent } from "@src/lib/ui/Card";
import Text from "@src/lib/ui/Text";
import { cn } from "@src/lib/utils/cn";
import RecommendationsBoard from "@src/features/recommendations/RecommendationsBoard";
import RecommendationBrandLogo, {
  getRecommendationBrand,
} from "./RecommendationBrandLogo";
import { STATUS_DISPLAY } from "./recommendationStatusDisplay";
import { Button } from "@src/lib/ui/Button";
import Table from "@src/lib/ui/Table";
import TopNavPageHeader from "@src/features/app/top-nav/TopNavPageHeader";
import {
  RecommendationsMockDataToggle,
  useRecommendationsMockData,
} from "@src/features/recommendations/RecommendationsMockDataContext";
import { getMockRecommendations } from "./recommendationsMockData";
import ReportingFilterDropdown, {
  ReportingFilterOption,
} from "@src/features/filters/ui/ReportingFilterDropdown";
import useLocations from "@src/features/locations/hooks/useLocations";
import useLocationGroups from "@src/features/locations/hooks/useLocationGroups";
import {
  TruthyNumberArrayParam,
  StringArrayParam,
} from "@src/lib/utils/queryParams";
import UnmatchedIntegrationLocationsBanner from "./UnmatchedIntegrationLocationsBanner";

const REDESIGNED_TABS = ["reviews", "data-sources", "analyses"] as const;

type RedesignedTab = (typeof REDESIGNED_TABS)[number];

const GUEST_FEEDBACK_TABS: { label: string; value: RedesignedTab }[] = [
  { label: "Reviews", value: "reviews" },
  { label: "Data sources", value: "data-sources" },
  { label: "Analysis history", value: "analyses" },
];

export type { RedesignedTab };

const PAGE_MAX_WIDTH = "max-w-[1280px]";
// How often to check whether the recommendation agent is running, and how often
// to refresh recommendations while it is.
const AGENT_STATUS_POLL_INTERVAL_MS = 20_000;
const AGENT_RUNNING_RECOMMENDATIONS_POLL_INTERVAL_MS = 15_000;

function getSelectedTab(tab: string): RedesignedTab {
  if (REDESIGNED_TABS.includes(tab as RedesignedTab)) {
    return tab as RedesignedTab;
  }
  if (["integrations", "syncs", "csv-uploads"].includes(tab)) {
    return "data-sources";
  }
  return "reviews";
}

export { getSelectedTab };

function getPriorityRank(priority: ActionItemPriority): number {
  switch (priority) {
    case ActionItemPriority.High:
      return 0;
    case ActionItemPriority.Medium:
      return 1;
    case ActionItemPriority.Low:
      return 2;
  }
}

function getDetectedAt(rec: RecommendationListItem): string | null {
  return rec.analysisRun?.completedAt ?? rec.analysisRun?.startedAt ?? null;
}

const SETUP_POLL_INTERVAL_MS = 30_000;

const GuestFeedbackView: React.FC = () => {
  const [activeTab, setActiveTab] = useQueryParam(
    "tab",
    withDefault(StringParam, "reviews"),
  );
  const selectedTab = getSelectedTab(activeTab);
  const [oAuthId, setOAuthId] = useQueryParam("oauth-id", StringParam);
  const subheaderTarget = usePortalTarget("top-nav-subheader");
  const { isCsvImportInProgress } = useGuestFeedbackCsvImport();

  const { loading: setupLoading, hasAnyDataSource } =
    useGuestFeedbackSetupStatus({
      pollInterval: isCsvImportInProgress
        ? CSV_IMPORT_POLL_INTERVAL_MS
        : SETUP_POLL_INTERVAL_MS,
    });
  const showGettingStarted = !setupLoading && !hasAnyDataSource;

  return (
    <ListPage className="[&>div]:!gap-4 [&>div]:!p-4 [&>div]:!pt-3">
      <GuestFeedbackPageHeader showActions={!showGettingStarted} />
      {!showGettingStarted &&
        subheaderTarget &&
        createPortal(
          <>
            {/* Selecting pushes a history entry, so arrows only move focus. */}
            <Tabs
              asChild
              activationMode="manual"
              value={selectedTab}
              onValueChange={setActiveTab}
            >
              <TabsList
                aria-label="Guest feedback views"
                data-testid="subheader-tabs"
                className="order-first h-full min-h-0 shrink-0"
              >
                {GUEST_FEEDBACK_TABS.map((tab) => (
                  <TabsTrigger
                    key={tab.value}
                    value={tab.value}
                    data-testid={`tab-${tab.value}`}
                  >
                    {tab.label}
                  </TabsTrigger>
                ))}
              </TabsList>
            </Tabs>
            {selectedTab === "reviews" && <RecommendationsMockDataToggle />}
          </>,
          subheaderTarget,
        )}
      <div className={cn("mx-auto flex w-full flex-col gap-6", PAGE_MAX_WIDTH)}>
        {oAuthId && (
          <GoogleBusinessAccountDialog
            oAuthId={oAuthId}
            onClose={() => setOAuthId(null)}
          />
        )}
        {!showGettingStarted && <UnmatchedIntegrationLocationsBanner />}
        {setupLoading ? (
          <EmptyState loading />
        ) : showGettingStarted ? (
          <>
            <RecommendationsMockDataToggle />
            <GuestFeedbackGettingStarted />
          </>
        ) : selectedTab === "reviews" ? (
          <GuestFeedbackReviewsTab />
        ) : selectedTab === "analyses" ? (
          <GuestFeedbackAnalysesTab />
        ) : (
          <>
            <GuestFeedbackUploadBatches />
            <DataSourcesTab />
          </>
        )}
      </div>
    </ListPage>
  );
};

type RecommendationListItem = NonNullable<
  ReturnType<typeof useGuestFeedbackRecommendationsQuery>["data"]
>["GuestFeedbackRecommendations"][number];

export type { RecommendationListItem };

type StatusFilter =
  | "all"
  | "pending"
  | "inProgress"
  | "measuring"
  | "completed"
  | "dismissed";

const STATUS_FILTERS: StatusFilter[] = [
  "all",
  "pending",
  "inProgress",
  "measuring",
  "completed",
  "dismissed",
];
const STATUS_FILTER_LABEL: Record<StatusFilter, string> = {
  all: "All",
  pending: "Inbox",
  inProgress: "In progress",
  measuring: "Measuring impact",
  completed: "Done",
  dismissed: "Dismissed",
};

export type { StatusFilter };
export { STATUS_FILTERS, STATUS_FILTER_LABEL };

// Empty-state copy per status, so a filtered/standalone status page reads for
// its own state instead of the generic "no recommendations match these filters".
const STATUS_EMPTY_STATE: Record<
  StatusFilter,
  { title: string; description: string }
> = {
  all: {
    title: "No recommendations match these filters",
    description: "Clear filters to see every open recommendation.",
  },
  pending: {
    title: "Your inbox is empty",
    description:
      "New recommendations from Opus will show up here as they're generated.",
  },
  inProgress: {
    title: "Nothing in progress yet",
    description:
      "When you take action on a recommendation, it moves here while you implement it.",
  },
  measuring: {
    title: "Nothing measuring impact yet",
    description:
      "When you start measuring impact, the recommendation moves here so Opus can track its effect.",
  },
  completed: {
    title: "Nothing marked done yet",
    description: "Recommendations you mark as done will show up here.",
  },
  dismissed: {
    title: "No dismissed recommendations",
    description: "Recommendations you dismiss will show up here.",
  },
};

const GUEST_FEEDBACK_EMPTY_STATE_DESCRIPTION =
  "Connect Guest Feedback under Signals to start generating recommendations. Once a source is connected, Opus surfaces recurring issues here with evidence, suggested actions, and follow-up tracking.";

const RECOMMENDATIONS_EMPTY_STATE_DESCRIPTION =
  "When Opus detects new training opportunities from your connected signals, you’ll see them in the Inbox.";

export { RECOMMENDATIONS_EMPTY_STATE_DESCRIPTION };

const OpportunitiesEmptyState: React.FC<{ description: string }> = ({
  description,
}) => {
  return (
    <Card variant="elevated">
      <CardContent className="p-8">
        <EmptyState>
          <EmptyStateTitle>No recommendations yet</EmptyStateTitle>
          <EmptyStateDescription>{description}</EmptyStateDescription>
        </EmptyState>
      </CardContent>
    </Card>
  );
};

function isStatusFilter(value: string): value is StatusFilter {
  return (STATUS_FILTERS as string[]).includes(value);
}

export { isStatusFilter };

function statusMatchesFilter(
  status: GuestFeedbackCorrectiveActionStatus,
  filter: StatusFilter,
): boolean {
  switch (filter) {
    case "pending":
      return status === GuestFeedbackCorrectiveActionStatus.Pending;
    case "inProgress":
      return status === GuestFeedbackCorrectiveActionStatus.InProgress;
    case "measuring":
      return status === GuestFeedbackCorrectiveActionStatus.MeasuringImpact;
    case "completed":
      return status === GuestFeedbackCorrectiveActionStatus.Completed;
    case "dismissed":
      return status === GuestFeedbackCorrectiveActionStatus.Rejected;
    case "all":
      return status !== GuestFeedbackCorrectiveActionStatus.Rejected;
  }
}

export { statusMatchesFilter };

type RecommendationColumn =
  | "recommendation"
  | "brand"
  | "locations"
  | "detected"
  | "started"
  | "implemented"
  | "markedDone";

const RECOMMENDATION_COLUMNS: RecommendationColumn[] = [
  "recommendation",
  "brand",
  "locations",
  "detected",
  "started",
  "implemented",
  "markedDone",
];

const RECOMMENDATION_COLUMN_TITLES: Record<RecommendationColumn, string> = {
  recommendation: "Recommendation",
  brand: "Brand",
  locations: "Locations",
  detected: "Created",
  started: "Started",
  implemented: "Implemented",
  markedDone: "Marked Done",
};

// Shared mm/dd/yyyy formatter for the recommendation table date columns
// (Created, Implemented).
const parseDisplayDate = (value: string): Date => {
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  if (dateOnly) {
    return new Date(
      Number(dateOnly[1]),
      Number(dateOnly[2]) - 1,
      Number(dateOnly[3]),
    );
  }
  return new Date(value);
};

const formatTableDate = (value: string): string =>
  parseDisplayDate(value).toLocaleDateString("en-US", {
    month: "numeric",
    day: "numeric",
    year: "numeric",
  });

const FilterPill: React.FC<{
  active: boolean;
  onClick: () => void;
  label: string;
  count?: number;
}> = ({ active, onClick, label, count }) => {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex h-7 items-center rounded-md border px-2.5 transition-colors",
        active
          ? "border-gray-12 bg-gray-12 text-background"
          : "border-gray-2 bg-background text-foreground hover:bg-gray-1",
      )}
    >
      <Text type="P3" fontWeight={active ? "SemiBold" : "Medium"}>
        {label}
        {count !== undefined && (
          <span
            className={cn(
              "ml-1.5",
              active ? "text-background/70" : "text-muted-foreground",
            )}
          >
            {count}
          </span>
        )}
      </Text>
    </button>
  );
};

export const OpportunitiesList: React.FC<{
  hideStatusFilters?: boolean;
  emptyStateDescription?: string;
}> = ({
  hideStatusFilters = false,
  emptyStateDescription = GUEST_FEEDBACK_EMPTY_STATE_DESCRIPTION,
}) => {
  const { useMockData } = useRecommendationsMockData();
  const {
    data,
    loading: queryLoading,
    startPolling,
    stopPolling,
  } = useGuestFeedbackRecommendationsQuery({
    variables: { input: {} },
    skip: useMockData,
  });

  const loading = queryLoading && !useMockData;
  const recommendations = useMemo(
    () =>
      useMockData
        ? getMockRecommendations()
        : data?.GuestFeedbackRecommendations ?? [],
    [data, useMockData],
  );

  // The backend only stamps a recommendation's locationGroup with a brand
  // (never a region/franchise) and only for brand-enabled orgs, so the presence
  // of a brand on any recommendation is our signal to surface brand info.
  const showBrand = useMemo(
    () => recommendations.some((rec) => getRecommendationBrand(rec) != null),
    [recommendations],
  );

  // The recommendation agent records each run as a GuestFeedbackAnalysisRun. We
  // judge status off the most recent run: in flight when it has started but
  // neither completed nor errored, failed when it has an error. Poll so we
  // notice it start/finish without a manual refresh.
  const { data: analysisRunsData } = useGuestFeedbackAnalysisRunStatusQuery({
    pollInterval: AGENT_STATUS_POLL_INTERVAL_MS,
    skip: useMockData,
  });
  const mostRecentAnalysisRun = useMemo(() => {
    const runs = analysisRunsData?.GuestFeedbackAnalysisRuns ?? [];
    return [...runs].sort(
      (a, b) =>
        new Date(b.startedAt).getTime() - new Date(a.startedAt).getTime(),
    )[0];
  }, [analysisRunsData]);
  const isAgentRunning =
    !!mostRecentAnalysisRun &&
    !mostRecentAnalysisRun.completedAt &&
    !mostRecentAnalysisRun.error;
  const agentErrored = !!mostRecentAnalysisRun?.error;

  // While the agent is running, poll the recommendations themselves so new ones
  // stream in (and the list is fresh once the run completes).
  React.useEffect(() => {
    if (isAgentRunning) {
      startPolling(AGENT_RUNNING_RECOMMENDATIONS_POLL_INTERVAL_MS);
    } else {
      stopPolling();
    }
    return () => stopPolling();
  }, [isAgentRunning, startPolling, stopPolling]);

  const [statusParam, setStatusParam] = useQueryParam(
    "status",
    withDefault(StringParam, "all"),
  );
  const statusFilter: StatusFilter = isStatusFilter(statusParam)
    ? statusParam
    : "all";
  const setStatusFilter = (next: StatusFilter) =>
    setStatusParam(next === "all" ? undefined : next);

  const [locationIds, setLocationIds] = useQueryParam(
    "locationIds",
    withDefault(TruthyNumberArrayParam, [] as number[]),
  );
  const [brandIds, setBrandIds] = useQueryParam(
    "brandIds",
    withDefault(StringArrayParam, [] as string[]),
  );

  const { locations } = useLocations();
  const { lgTypeToLgs } = useLocationGroups();

  const locationOptions: ReportingFilterOption[] = useMemo(
    () =>
      locations.map((location) => ({
        label: location.name,
        value: location.id.toString(),
        type: "Location",
      })),
    [locations],
  );
  const brandOptions: ReportingFilterOption[] = useMemo(
    () =>
      (lgTypeToLgs[LocationGroupType.Brand] ?? []).map((brand) => ({
        label: brand.name,
        value: brand.id,
        type: "Brand",
      })),
    [lgTypeToLgs],
  );

  // Location/brand filters narrow the pool before status filtering, so pill
  // counts and the board/table both reflect the location- and brand-scoped set.
  const locationAndBrandFilteredRecommendations = useMemo(
    () =>
      recommendations.filter((rec) => {
        const matchesLocation =
          locationIds.length === 0 ||
          rec.locations.some((location) => locationIds.includes(location.id));
        const matchesBrand =
          brandIds.length === 0 ||
          (rec.locationGroup != null &&
            brandIds.includes(rec.locationGroup.id));
        return matchesLocation && matchesBrand;
      }),
    [recommendations, locationIds, brandIds],
  );

  const statusCounts: Record<StatusFilter, number> = useMemo(
    () => ({
      all: locationAndBrandFilteredRecommendations.filter((rec) =>
        statusMatchesFilter(rec.status, "all"),
      ).length,
      pending: locationAndBrandFilteredRecommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Pending,
      ).length,
      inProgress: locationAndBrandFilteredRecommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.InProgress,
      ).length,
      measuring: locationAndBrandFilteredRecommendations.filter(
        (rec) =>
          rec.status === GuestFeedbackCorrectiveActionStatus.MeasuringImpact,
      ).length,
      completed: locationAndBrandFilteredRecommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Completed,
      ).length,
      dismissed: locationAndBrandFilteredRecommendations.filter(
        (rec) => rec.status === GuestFeedbackCorrectiveActionStatus.Rejected,
      ).length,
    }),
    [locationAndBrandFilteredRecommendations],
  );

  const filteredRecommendations = useMemo(() => {
    const sorted = [...locationAndBrandFilteredRecommendations].sort((a, b) => {
      const priorityDiff =
        getPriorityRank(a.priority) - getPriorityRank(b.priority);
      if (priorityDiff !== 0) return priorityDiff;
      const aDetected = getDetectedAt(a);
      const bDetected = getDetectedAt(b);
      if (!aDetected && !bDetected) return 0;
      if (!aDetected) return 1;
      if (!bDetected) return -1;
      return new Date(bDetected).getTime() - new Date(aDetected).getTime();
    });
    return sorted.filter((rec) =>
      statusMatchesFilter(rec.status, statusFilter),
    );
  }, [locationAndBrandFilteredRecommendations, statusFilter]);

  const hasLocationOrBrandFiltersApplied =
    locationIds.length > 0 || brandIds.length > 0;
  const hasFiltersApplied =
    statusFilter !== "all" || hasLocationOrBrandFiltersApplied;
  const clearFilters = () => {
    setStatusFilter("all");
    setLocationIds([]);
    setBrandIds([]);
  };
  const emptyState = hasLocationOrBrandFiltersApplied
    ? STATUS_EMPTY_STATE.all
    : STATUS_EMPTY_STATE[statusFilter];

  return (
    <div className="flex flex-col gap-5 pb-6">
      {/* Portals the total item count into the page-title zone as a gray pill,
          matching the library page headers. */}
      <TopNavPageHeader
        count={loading ? undefined : filteredRecommendations.length}
        countLoading={loading}
      />
      {isAgentRunning && (
        <div className="flex items-center gap-3 rounded-xl border border-blue-3 bg-blue-1 px-4 py-3">
          <Spinner size={20} className="shrink-0 text-blue-9" />
          <Text
            variant="fine-print-description"
            multiline
            className="text-blue-11"
          >
            <span className="font-semibold">Analyzing feedback…</span> Opus is
            reviewing your guest feedback and generating recommendations. New
            recommendations will appear here as they&apos;re ready.
          </Text>
        </div>
      )}
      {agentErrored && (
        <div className="flex items-center gap-3 rounded-xl border border-red-3 bg-red-1 px-4 py-3">
          <Text
            variant="fine-print-description"
            multiline
            className="text-red-11"
          >
            <span className="font-semibold">
              Recommendation analysis failed.
            </span>{" "}
            The last run didn&apos;t finish. We&apos;ll retry automatically —
            recommendations may be out of date until it succeeds.
          </Text>
        </div>
      )}
      <div className="flex flex-wrap items-center gap-2">
        {!hideStatusFilters && (
          <div className="mr-auto flex flex-wrap items-center gap-1.5">
            {STATUS_FILTERS.map((value) => (
              <FilterPill
                key={value}
                active={statusFilter === value}
                onClick={() => setStatusFilter(value)}
                label={STATUS_FILTER_LABEL[value]}
                count={loading ? undefined : statusCounts[value]}
              />
            ))}
          </div>
        )}
        <ReportingFilterDropdown
          options={locationOptions}
          selectedValues={locationIds.map(String)}
          onSelectionChange={(values) => setLocationIds(values.map(Number))}
          placeholder="All Locations"
          searchPlaceholder="Search locations..."
          noResultsText="No locations found."
        />
        {showBrand && (
          <ReportingFilterDropdown
            options={brandOptions}
            selectedValues={brandIds}
            onSelectionChange={setBrandIds}
            placeholder="All Brands"
            searchPlaceholder="Search brands..."
            noResultsText="No brands found."
          />
        )}
      </div>

      {loading ? (
        <div className="flex flex-col gap-3">
          {Array.from({ length: 4 }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full rounded-lg" />
          ))}
        </div>
      ) : recommendations.length === 0 ? (
        <OpportunitiesEmptyState description={emptyStateDescription} />
      ) : filteredRecommendations.length === 0 ? (
        <Card variant="elevated">
          <CardContent className="p-6">
            <EmptyState>
              <EmptyStateTitle>{emptyState.title}</EmptyStateTitle>
              <EmptyStateDescription>
                {emptyState.description}
              </EmptyStateDescription>
              {hasFiltersApplied && (
                <Button
                  variant="outline"
                  size="sm"
                  className="mt-2"
                  onClick={clearFilters}
                >
                  Show all recommendations
                </Button>
              )}
            </EmptyState>
          </CardContent>
        </Card>
      ) : (
        <div className="flex flex-col gap-4">
          {statusFilter === "all" ? (
            <RecommendationsBoard
              recommendations={filteredRecommendations}
              showBrand={showBrand}
            />
          ) : (
            <Table<RecommendationListItem, RecommendationColumn>
              allItems={filteredRecommendations}
              allColumns={RECOMMENDATION_COLUMNS}
              visibleColumns={() => {
                const columns: RecommendationColumn[] = ["recommendation"];
                if (showBrand) columns.push("brand");
                columns.push("locations");
                // "Created" (when the recommendation was first surfaced). Shown
                // on the Inbox view, and on Dismissed as a fallback since the
                // backend does not expose a dismissed-at timestamp.
                if (statusFilter === "pending" || statusFilter === "dismissed")
                  columns.push("detected");
                if (statusFilter === "inProgress") columns.push("started");
                if (statusFilter === "measuring") columns.push("implemented");
                // "Marked Done" (when the action was completed) is only relevant
                // on the Done view.
                if (statusFilter === "completed") columns.push("markedDone");
                return columns;
              }}
              itemId={(rec) => rec.id}
              showCount={false}
              columnTitles={RECOMMENDATION_COLUMN_TITLES}
              rowClickLink={(rec) => ({
                pathname: "/guest-feedback/recommendations/[id]",
                // Carry the active tab so the detail page's back link can
                // return to it (absent = the "All" view).
                query: { id: rec.id, status: statusFilter },
              })}
              horizontalScroll
              trailingEmptyColumnWidth={16}
              growableColumn="recommendation"
              responsiveness={{
                recommendation: { minWidth: 320 },
                brand: { minWidth: 120, width: 180 },
                locations: { minWidth: 90, width: 120 },
                detected: { minWidth: 90, width: 120 },
                started: { minWidth: 90, width: 120 },
                implemented: { minWidth: 110, width: 140 },
                markedDone: { minWidth: 110, width: 140 },
              }}
              renderFns={{
                recommendation: (rec) => {
                  const status = STATUS_DISPLAY[rec.status];
                  const StatusIcon = status.Icon;
                  return (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <span
                        className={cn(
                          "flex size-5 shrink-0 items-center justify-center rounded-full",
                          status.pillClassName,
                        )}
                      >
                        <StatusIcon
                          className={cn("size-3", status.iconClassName)}
                        />
                      </span>
                      <Text
                        variant="compact-title"
                        ellipsis
                        ellipsisParentFlexDirection="row"
                        className="text-foreground"
                      >
                        {rec.title}
                      </Text>
                    </div>
                  );
                },
                brand: (rec) => {
                  const brand = getRecommendationBrand(rec);
                  if (!brand) return null;
                  return (
                    <div className="flex min-w-0 flex-1 items-center gap-2">
                      <RecommendationBrandLogo recommendation={rec} />
                      <Text
                        variant="compact-description"
                        className="text-foreground"
                        ellipsis
                        ellipsisParentFlexDirection="row"
                      >
                        {brand.name}
                      </Text>
                    </div>
                  );
                },
                locations: (rec) => (
                  <Text variant="compact-description">
                    {rec.affectedLocations.length}
                  </Text>
                ),
                started: (rec) =>
                  rec.acceptedAt ? formatTableDate(rec.acceptedAt) : "—",
                implemented: (rec) =>
                  rec.measuringImpactStartedOn
                    ? formatTableDate(rec.measuringImpactStartedOn)
                    : "—",
                markedDone: (rec) =>
                  rec.completedAt ? formatTableDate(rec.completedAt) : "—",
                detected: (rec) => {
                  const detectedAt = getDetectedAt(rec);
                  return detectedAt ? formatTableDate(detectedAt) : "—";
                },
              }}
            />
          )}
        </div>
      )}
    </div>
  );
};

const DataSourcesTab: React.FC = () => {
  const {
    data: integrationsData,
    loading: integrationsLoading,
    error: integrationsError,
  } = useOrgGuestFeedbackIntegrationsQuery();
  const {
    data: csvUploadsData,
    loading: csvUploadsLoading,
    error: csvUploadsError,
  } = useOrgGuestFeedbackCsvUploadsQuery();
  const {
    data: syncsData,
    loading: syncsLoading,
    error: syncsError,
  } = useOrgGuestFeedbackSyncsQuery();

  if (
    (integrationsLoading && !integrationsData) ||
    (csvUploadsLoading && !csvUploadsData) ||
    (syncsLoading && !syncsData)
  ) {
    return (
      <div className="flex flex-col gap-3 pb-6">
        {Array.from({ length: 4 }).map((_, i) => (
          <Skeleton key={i} className="h-12 w-full rounded" />
        ))}
      </div>
    );
  }

  const sections = [
    (!!integrationsError ||
      (integrationsData?.OrgGuestFeedbackIntegrations ?? []).length > 0) && {
      key: "integrations",
      title: "Connected sources",
      description: "Manage connected review sources and location mappings.",
      content: <GuestFeedbackIntegrationsTab />,
    },
    (!!csvUploadsError ||
      (csvUploadsData?.OrgGuestFeedbackCsvUploads.totalCount ?? 0) > 0) && {
      key: "csv-uploads",
      title: "CSV uploads",
      description:
        "Review imported files, mapped locations, and imported review counts.",
      content: <GuestFeedbackCsvUploadsTab />,
    },
    (!!syncsError ||
      (syncsData?.OrgGuestFeedbackSyncs?.totalCount ?? 0) > 0) && {
      key: "syncs",
      title: "Sync history",
      description:
        "Check when connected sources imported reviews and whether syncs completed.",
      content: <GuestFeedbackSyncsTab />,
    },
  ].filter((section): section is Exclude<typeof section, false> => !!section);

  if (sections.length === 0) {
    return (
      <Card variant="elevated">
        <CardContent className="p-6">
          <EmptyState>
            <EmptyStateTitle>No data sources yet</EmptyStateTitle>
            <EmptyStateDescription>
              Add an integration or upload a CSV to start importing guest
              feedback.
            </EmptyStateDescription>
          </EmptyState>
        </CardContent>
      </Card>
    );
  }

  if (sections.length === 1) {
    return <div className="flex flex-col pb-6">{sections[0].content}</div>;
  }

  return (
    <div className="flex flex-col gap-8 pb-6">
      {sections.map((section) => (
        <DataSourceSection
          key={section.key}
          title={section.title}
          description={section.description}
        >
          {section.content}
        </DataSourceSection>
      ))}
    </div>
  );
};

const DataSourceSection: React.FC<{
  title: string;
  description: string;
  children: React.ReactNode;
}> = ({ title, description, children }) => {
  return (
    <section className="flex flex-col gap-3">
      <div className="flex flex-col gap-0.5">
        <Text variant="body-title">{title}</Text>
        <Text
          variant="fine-print-description"
          className="text-muted-foreground"
        >
          {description}
        </Text>
      </div>
      {children}
    </section>
  );
};

export default GuestFeedbackView;

gql`
  fragment GuestFeedbackRecommendationsListItem on GuestFeedbackCorrectiveAction {
    id
    title
    issueDescription
    priority
    status
    actionType
    affectedLocations
    correctiveActionGroupId
    audience
    theme {
      id
      name
    }
    sampleReviews {
      id
      review
    }
  }

  query GuestFeedbackRecommendations(
    $input: GuestFeedbackRecommendationsInput!
  ) {
    GuestFeedbackRecommendations(input: $input) {
      id
      title
      issueDescription
      priority
      status
      actionType
      affectedLocations
      acceptedAt
      completedAt
      measuringImpactStartedOn
      audience
      analysisRun {
        id
        startedAt
        completedAt
      }
      theme {
        id
        name
      }
      locationGroup {
        id
        name
        type
        brandDetails {
          id
          logoUrls {
            id
            thumb
            original
          }
        }
      }
      locations {
        id
        name
      }
      sampleReviews {
        id
        review
      }
    }
  }

  query GuestFeedbackAnalysisRunStatus {
    GuestFeedbackAnalysisRuns {
      id
      startedAt
      completedAt
      error
    }
  }

  query GuestFeedbackThemes($input: GuestFeedbackThemesInput!) {
    GuestFeedbackThemes(input: $input) {
      id
      name
      description
      feedbackCount
    }
  }

  mutation StartAIUpdateLibraryItem($input: AIUpdateLibraryItemInput!) {
    startAIUpdateLibraryItem(input: $input) {
      success
      error {
        message
      }
      clientFacingWorkflow {
        ...ClientFacingWorkflow
      }
    }
  }

  mutation LinkLibraryItemToRecommendation(
    $id: UUID!
    $input: UpdateGuestFeedbackCorrectiveActionInput!
  ) {
    updateGuestFeedbackCorrectiveAction(id: $id, input: $input) {
      success
      error {
        message
      }
      correctiveAction {
        id
        status
        libraryItem {
          id
          name {
            en
          }
        }
      }
    }
  }
`;
